import { c as _c } from "react-compiler-runtime";
import { useEffect } from 'react';
import { useCellSelectionDispatch } from '../../redux/reduxHooks';
import { useCellSelectionScopeId } from '../context/CellSelectionContext';
import { clearCellSelection } from '../redux/cellSelectionSlice';
import { CELL_SELECTABLE_ATTRIBUTE, CELL_ACTION_MENU_ATTRIBUTE } from '../../table/constants/cellSelectionAttributes';

/**
 * Clears the cell selection on interactions outside the selectable cells:
 *   - pressing Escape, and
 *   - a mousedown anywhere that isn't inside a selectable cell (checkboxes,
 *     headers, blank space, other UI).
 *
 * Only called from CellSelectionEffects, which is mounted only when cell
 * selection is enabled. The document mousedown listener runs after React's
 * synthetic per-cell handler (bubble order), so clicking a real cell selects
 * it and then this listener sees the cell and skips clearing.
 *
 * Scope-aware: skips clearing only when the clicked cell belongs to *this*
 * table's scope, so nested tables (e.g. an inline association table) each
 * maintain independent selections without bleeding into each other.
 */
export const useClearCellSelectionOnExternalInteraction = () => {
  const $ = _c(4);
  const dispatch = useCellSelectionDispatch();
  const scopeId = useCellSelectionScopeId();
  let t0;
  let t1;
  if ($[0] !== dispatch || $[1] !== scopeId) {
    t0 = () => {
      const handleKeyDown = e => {
        if (e.key === "Escape") {
          dispatch(clearCellSelection());
        }
      };
      const handleMouseDown = e_0 => {
        const {
          target
        } = e_0;
        if (target instanceof Element) {
          const selectable = target.closest(`[${CELL_SELECTABLE_ATTRIBUTE}]`);
          if ((selectable === null || selectable === void 0 ? void 0 : selectable.getAttribute(CELL_SELECTABLE_ATTRIBUTE)) === scopeId) {
            return;
          }
          if (target.closest(`[${CELL_ACTION_MENU_ATTRIBUTE}]`)) {
            return;
          }
        }
        dispatch(clearCellSelection());
      };
      document.addEventListener("keydown", handleKeyDown);
      document.addEventListener("mousedown", handleMouseDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
        document.removeEventListener("mousedown", handleMouseDown);
      };
    };
    t1 = [dispatch, scopeId];
    $[0] = dispatch;
    $[1] = scopeId;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
};